import { useState } from "react";

import ListUnsuitableContainer from "@/components/shared/business/ListUnsuitableContainer/ListUnsuitableContainer";
import { UNSUITABLE_REASON_KEYS } from "@/components/shared/business/UnsuitableReason/constants/unsuitableReason.constants";
import { useClassification } from "@/contexts/ClassificationContext";
import { usePressListFilterQuery } from "@/queries/hooks/usePressListFilter";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { formatDate } from "@utils/formatDate";
import { handleDraftError } from "@utils/handleDraftError";

import PressListCompanyShell from "./components/PressListCompanyShell/PressListCompanyShell";
import PressListPressTitleContainer from "./components/PressListPressTitleContainer/PressListPressTitleContainer";
import { PressListPublishableContainer } from "./components/PressListPublishableContainer/PressListPublishableContainer";
import PressListStatusContainer from "./components/PressListStatusContainer/PressListStatusContainer";
import * as S from "./PressListTableBody.style";
import { EMPTY_SHELL_VALUE } from "../../constants/pressListTable.constants";

const EDITOR_LIST = [
  "mcEditorName",
  "ftEditorName",
  "ftEditor2Name",
  "pbEditorName",
];

const WRITE_DATE_LIST = ["writeStartDt", "writeEndDt"];

const PressListTableBody = () => {
  const [activeModalPressId, setActiveModalPressId] = useState<number | null>(
    null,
  );

  const { pressClassificationData } = useClassification();

  const { data: pressListData, refetch: updatePressList } =
    usePressListFilterQuery();

  const getClassificationName = (code: string) => {
    if (!pressClassificationData) return EMPTY_SHELL_VALUE;
    return pressClassificationData[code] || EMPTY_SHELL_VALUE;
  };

  return (
    <S.PressListTableBody>
      {pressListData?.data.map((pressListItem) => {
        const isReviewRequired =
          pressListItem.reviewInformationList.filter(
            ({ deleted }) => deleted !== true,
          ).length > 0;
        const reviewIdList = pressListItem.reviewInformationList.map((review) =>
          Number(review.reviewId),
        );
        return (
          <S.PressListTableTr
            key={`row-${pressListItem.pressId}`}
            isError={handleDraftError(pressListItem)[0]}
          >
            <S.PressListTablePressId>
              #{pressListItem.pressId}
            </S.PressListTablePressId>
            <S.PressListTableDate>
              {pressListItem.pressDate}
            </S.PressListTableDate>
            <S.PressListTableCompany>
              <PressListCompanyShell
                companyName={pressListItem.companyName}
                targetGroup={pressListItem.targetGroup}
              />
            </S.PressListTableCompany>
            <S.PressListTablePublishable>
              <PressListPublishableContainer
                data={{
                  ...pressListItem,
                  publishable: pressListItem.publishable ?? false,
                  aiDone: pressListItem.aiDone ?? false,
                  reviewIdList: reviewIdList || null,
                }}
              />
            </S.PressListTablePublishable>
            <S.PressListTableUnsuitable
              isReviewRequired={isReviewRequired}
              isUnsuitable={
                pressListItem.draftArticleStatusHighest ===
                DRAFT_STATUS_CODE.unsuitable
              }
              isModalOpen={activeModalPressId === pressListItem.pressId}
            >
              <ListUnsuitableContainer
                onModalStateChange={(isOpen) => {
                  if (isOpen) {
                    setActiveModalPressId(pressListItem.pressId);
                  } else if (activeModalPressId === pressListItem.pressId) {
                    setActiveModalPressId(null);
                  }
                }}
                id={Number(pressListItem.pressId)}
                reviewRequiredText={
                  isReviewRequired
                    ? `Overlapped\nID: #${reviewIdList.join(
                        ", #",
                      )}\n(Review Required)`
                    : ""
                }
                articleRejectReason={pressListItem?.articleRejectReason || ""}
                duplArticleId={Number(pressListItem.duplArticleId)}
                draftArticleStatusHighest={
                  pressListItem.draftArticleStatusHighest
                }
                mcEditorName={pressListItem.mcEditorName}
                aiDone={pressListItem.aiDone || false}
                unsuitableReasonUsageType={UNSUITABLE_REASON_KEYS.pressList}
                reviewIdList={reviewIdList || null}
              />
            </S.PressListTableUnsuitable>
            <PressListPressTitleContainer
              pressListItem={pressListItem}
              updatePressList={updatePressList}
              pressListData={pressListData?.data || []}
            />
            <S.PressListTableClassification>
              {getClassificationName(pressListItem.classificationAi)}
            </S.PressListTableClassification>

            <S.ClassificationTableData
              status={pressListItem.draftArticleStatus}
            >
              {pressListItem.classificationAi !== "C801" &&
              pressListItem.classificationAi !== "C802"
                ? getClassificationName(pressListItem.classificationManual)
                : "N/A"}
            </S.ClassificationTableData>

            <S.StatusTableData
              key={pressListItem.pressId}
              code={pressListItem.draftArticleStatusHighest}
            >
              <PressListStatusContainer data={pressListItem} />
            </S.StatusTableData>
            {WRITE_DATE_LIST.map((dateKey) => (
              <S.PressListTableDate key={dateKey}>
                {pressListItem[dateKey as keyof typeof pressListItem]
                  ? formatDate(
                      pressListItem[
                        dateKey as keyof typeof pressListItem
                      ] as string,
                      "YY.MM.DD HH:mm:ss",
                    )
                  : EMPTY_SHELL_VALUE}
              </S.PressListTableDate>
            ))}
            {EDITOR_LIST.map((editor) => (
              <S.PressListTableEditor key={editor}>
                {(pressListItem[
                  editor as keyof typeof pressListItem
                ] as unknown as string) ?? EMPTY_SHELL_VALUE}
              </S.PressListTableEditor>
            ))}
          </S.PressListTableTr>
        );
      })}
    </S.PressListTableBody>
  );
};

export default PressListTableBody;
